import {
  InputFieldType,
  ValidationResult,
  WorkflowInputField,
} from './workflow-input-editor.types';

export const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === 'object' && val !== null && !Array.isArray(val);

export const randomId = () => Math.random().toString(36).slice(2, 10);

const mapPropertiesToFields = (
  props: Record<string, unknown>,
  requiredSet: Set<string>
): WorkflowInputField[] => {
  return Object.entries(props).map(([name, value], idx) => {
    const prop = isPlainObject(value) ? (value as Record<string, unknown>) : {};
    const typeRaw = prop.type;
    const type: InputFieldType =
      typeRaw === 'number' ||
      typeRaw === 'integer' ||
      typeRaw === 'boolean' ||
      typeRaw === 'object' ||
      typeRaw === 'array'
        ? typeRaw
        : 'string';

    const enumValues = Array.isArray(prop.enum)
      ? (prop.enum as unknown[]).filter(
          (v): v is string | number =>
            typeof v === 'string' || typeof v === 'number'
        )
      : undefined;

    const itemsType =
      type === 'array' && isPlainObject(prop.items)
        ? (prop.items as Record<string, unknown>).type
        : undefined;

    const subProperties =
      type === 'object' && isPlainObject(prop.properties)
        ? (prop.properties as Record<string, unknown>)
        : undefined;
    const subRequiredRaw =
      type === 'object' &&
      Array.isArray((prop as { required?: unknown }).required)
        ? ((prop as { required?: string[] }).required as string[])
        : [];
    const subFields =
      type === 'object' && subProperties
        ? mapPropertiesToFields(
            subProperties,
            new Set(
              subRequiredRaw.filter((k): k is string => typeof k === 'string')
            )
          )
        : undefined;

    const id = `${name}-${idx}-${randomId()}`;

    return {
      id,
      name,
      type,
      required: requiredSet.has(name),
      description:
        typeof prop.description === 'string' ? prop.description : undefined,
      format: typeof prop.format === 'string' ? prop.format : undefined,
      enumValues: enumValues as string[] | undefined,
      min:
        typeof (prop as { minimum?: unknown }).minimum === 'number'
          ? (prop as { minimum: number }).minimum
          : undefined,
      max:
        typeof (prop as { maximum?: unknown }).maximum === 'number'
          ? (prop as { maximum: number }).maximum
          : undefined,
      minLength:
        typeof (prop as { minLength?: unknown }).minLength === 'number'
          ? (prop as { minLength: number }).minLength
          : undefined,
      maxLength:
        typeof (prop as { maxLength?: unknown }).maxLength === 'number'
          ? (prop as { maxLength: number }).maxLength
          : undefined,
      minItems:
        typeof (prop as { minItems?: unknown }).minItems === 'number'
          ? (prop as { minItems: number }).minItems
          : undefined,
      maxItems:
        typeof (prop as { maxItems?: unknown }).maxItems === 'number'
          ? (prop as { maxItems: number }).maxItems
          : undefined,
      allowAdditionalProps:
        typeof prop.additionalProperties === 'boolean'
          ? (prop.additionalProperties as boolean)
          : undefined,
      itemType:
        type === 'array' && typeof itemsType === 'string'
          ? (itemsType as InputFieldType)
          : undefined,
      subFields,
    };
  });
};

export const schemaToFields = (
  schema?: Record<string, unknown> | null
): WorkflowInputField[] => {
  if (!schema || !isPlainObject(schema)) return [];
  const propsRaw = (schema as { properties?: unknown }).properties;
  const requiredRaw = (schema as { required?: unknown }).required;
  const requiredSet = new Set(
    Array.isArray(requiredRaw)
      ? (requiredRaw as unknown[]).filter(
          (k): k is string => typeof k === 'string'
        )
      : []
  );

  if (!propsRaw || !isPlainObject(propsRaw)) return [];
  const props = propsRaw as Record<string, unknown>;

  return mapPropertiesToFields(props, requiredSet);
};

const fieldToSchemaProperty = (
  field: WorkflowInputField
): Record<string, unknown> => {
  const prop: Record<string, unknown> = {
    type: field.type,
  };

  if (field.description) prop.description = field.description;
  if (field.type === 'string') {
    if (field.format) prop.format = field.format;
    if (field.enumValues && field.enumValues.length > 0) {
      prop.enum = field.enumValues;
    }
    if (typeof field.minLength === 'number') prop.minLength = field.minLength;
    if (typeof field.maxLength === 'number') prop.maxLength = field.maxLength;
  }

  if (field.type === 'number' || field.type === 'integer') {
    if (typeof field.min === 'number') prop.minimum = field.min;
    if (typeof field.max === 'number') prop.maximum = field.max;
  }

  if (field.type === 'object') {
    if (typeof field.allowAdditionalProps === 'boolean') {
      prop.additionalProperties = field.allowAdditionalProps;
    }
    if (field.subFields && field.subFields.length > 0) {
      const child = buildSchemaFromFields(field.subFields);
      prop.properties = child.properties;
      if (child.required && child.required.length > 0) {
        prop.required = child.required;
      }
    }
  }

  if (field.type === 'array') {
    prop.items = {
      type: field.itemType || 'string',
    };
    if (typeof field.minItems === 'number') prop.minItems = field.minItems;
    if (typeof field.maxItems === 'number') prop.maxItems = field.maxItems;
  }

  return prop;
};

const buildSchemaFromFields = (
  fields: WorkflowInputField[]
): { properties: Record<string, unknown>; required: string[] } => {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  fields.forEach((field) => {
    properties[field.name] = fieldToSchemaProperty(field);
    if (field.required) required.push(field.name);
  });

  return { properties, required };
};

export const fieldsToSchema = (
  fields: WorkflowInputField[]
): Record<string, unknown> => {
  const { properties, required } = buildSchemaFromFields(fields);
  const schema: Record<string, unknown> = {
    type: 'object',
    properties,
  };
  if (required.length > 0) schema.required = required;
  return schema;
};

export const validateFields = (
  fields: WorkflowInputField[]
): ValidationResult => {
  const globalErrors: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  if (fields.length > 20) {
    globalErrors.push('No se permiten más de 20 campos de entrada.');
  }

  const nameSet = new Set<string>();
  for (const field of fields) {
    const fieldErrs: string[] = [];
    if (!field.name.trim()) {
      fieldErrs.push('El campo debe tener un nombre.');
    }
    if (nameSet.has(field.name.trim())) {
      fieldErrs.push(`El nombre "${field.name}" está duplicado.`);
    }
    nameSet.add(field.name.trim());

    if (
      field.subFields &&
      field.subFields.length > 0 &&
      field.subFields.length > 20
    ) {
      fieldErrs.push(
        `El campo "${field.name}" no puede tener más de 20 subcampos.`
      );
    }

    if (field.enumValues && field.enumValues.length > 0) {
      const enumSet = new Set<string>();
      for (const ev of field.enumValues) {
        if (!ev.trim()) {
          fieldErrs.push(
            `El campo "${field.name}" tiene un valor permitido vacío.`
          );
        }
        if (enumSet.has(ev)) {
          fieldErrs.push(
            `El campo "${field.name}" tiene valores permitidos duplicados.`
          );
          break;
        }
        enumSet.add(ev);
      }
    }

    if (
      (field.type === 'number' || field.type === 'integer') &&
      typeof field.min === 'number' &&
      typeof field.max === 'number' &&
      field.min > field.max
    ) {
      fieldErrs.push(
        `El mínimo no puede ser mayor que el máximo en el campo "${field.name}".`
      );
    }

    if (
      field.type === 'string' &&
      typeof field.minLength === 'number' &&
      typeof field.maxLength === 'number' &&
      field.minLength > field.maxLength
    ) {
      fieldErrs.push(
        `La longitud mínima no puede ser mayor que la máxima en "${field.name}".`
      );
    }

    if (
      field.type === 'array' &&
      typeof field.minItems === 'number' &&
      typeof field.maxItems === 'number' &&
      field.minItems > field.maxItems
    ) {
      fieldErrs.push(
        `El mínimo de ítems no puede ser mayor que el máximo en "${field.name}".`
      );
    }

    if (field.type === 'array' && !field.itemType) {
      fieldErrs.push(`El campo "${field.name}" debe tener un tipo de ítem.`);
    }

    if (field.type === 'object' && field.subFields && field.subFields.length) {
      const subValidation = validateFields(field.subFields);
      if (subValidation.globalErrors.length) {
        subValidation.globalErrors.forEach((err) =>
          fieldErrs.push(`Subcampos (${field.name}): ${err}`)
        );
      }
      Object.values(subValidation.fieldErrors).forEach((errs) => {
        errs.forEach((err) =>
          fieldErrs.push(`Subcampo (${field.name}): ${err}`)
        );
      });
    }

    if (fieldErrs.length) {
      fieldErrors[field.id] = fieldErrs;
    }
  }

  return {
    globalErrors: Array.from(new Set(globalErrors)),
    fieldErrors,
  };
};
